import { useMaterialReactTable } from "material-react-table";
import { localizationTextObject } from "../../../utils/constants";
import EditRowDialog from "./EditRowDialog";
import { COLORS, FONT_PROPS } from "../../../styles/colors";
import { AddButton, DeleteButton } from "../../common/Buttons";
import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@mui/material";
import { ModeEdit } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

export function useTableInstance({
  columns,
  data,
  handleCreateOrder,
  handleDeleteSelected,
  handleUpdateOrder,
  CreateRowDialog,
  item,
  queryClient,
  setLoading,
  handleUpdateOrderList,
  isFetching,
  onDataChange,
  pagination,
  setPagination,
  openSnackbar,
  rowSelection,
  setRowSelection,
  updateRowExcludedStatus,
}) {
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState({});
  const [lastClickedIndex, setLastClickedIndex] = useState(null);
  const tableInstanceRef = useRef(null);
  const ToolbarActions = ({ table }) => {
    const selectedRows = table.getSelectedRowModel().rows;
    const hasSelection = selectedRows.length > 0;
    const handleBulkApply = () => {
      selectedRows.forEach((row) => {
        const orderId = row.original.order_id;
        row.original.is_excluded = false;
        updateRowExcludedStatus(orderId, false);
      });
      setSelectedRowIds({});
      openSnackbar(`${selectedRows.length}개 항목을 적용으로 변경했습니다.`);
    };
    const handleBulkExclude = () => {
      selectedRows.forEach((row) => {
        const orderId = row.original.order_id;
        row.original.is_excluded = true;
        updateRowExcludedStatus(orderId, true);
      });
      setSelectedRowIds({});
      openSnackbar(`${selectedRows.length}개 항목을 미적용으로 변경했습니다.`);
    };
    const handleBulkEdit = () => {
      setBulkEditModalOpen(true);
    };
    return (
      <div className="toolbar_container">
        <Button
          variant="contained"
          color="success"
          onClick={handleBulkApply}
          disabled={!hasSelection}
          size="small"
          startIcon={<CheckIcon />}
        >
          선택적용
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleBulkExclude}
          disabled={!hasSelection}
          size="small"
          startIcon={<CloseIcon />}
        >
          선택미적용
        </Button>
        <AddButton
          onClick={() => {
            table.setCreatingRow(true);
          }}
        />
        <DeleteButton
          onClick={() => handleDeleteSelected(table)}
          disabled={!hasSelection}
        />
        <Button
          variant="contained"
          color="info"
          onClick={handleBulkEdit}
          disabled={!hasSelection}
          startIcon={<ModeEdit />}
          size="small"
        >
          선택 수정
        </Button>
      </div>
    );
  };
  const handleRowClick = useCallback(
    ({ event, row }) => {
      const index = row.index;
      const rowId = row.id;
      const tableInstance = tableInstanceRef.current;
      if (!tableInstance) return;
      const allRows = tableInstance.getRowModel().rows;
      if (event.shiftKey && lastClickedIndex !== null) {
        const start = Math.min(lastClickedIndex, index);
        const end = Math.max(lastClickedIndex, index);
        const newSelected = {};
        for (let i = start; i <= end; i++) {
          newSelected[allRows[i].id] = true;
        }
        setSelectedRowIds(newSelected);
      } else if (event.ctrlKey || event.metaKey) {
        setSelectedRowIds((prev) => ({
          ...prev,
          [rowId]: !prev[rowId],
        }));
        setLastClickedIndex(index);
      } else {
        setSelectedRowIds({ [rowId]: true });
        setLastClickedIndex(index);
      }
    },
    [lastClickedIndex]
  );
  const table = useMaterialReactTable({
    columns,
    data,
    enableColumnFilters: true,
    enableColumnFilterModes: true,
    enableColumnOrdering: false,
    enableStickyHeader: true,
    enableColumnActions: false,
    enableMultiRowSelection: false,
    enableRowSelection: false,
    onEditingRowSave: ({ row, values, table }) => {
      const orderId = row.original.order_id;
      const rowStatus = row.original.status;
      const beforeCalStatus = rowStatus === 1;
      const obj = {
        order_name: row.order_name ?? row.original.order_name,
        color: row.color ?? row.original.color,
        item_id: row.item_id ?? row.original.item_id,
        qty: row.qty ?? row.original.qty,
        start_date: row.start_date ?? row.original.start_date,
        due_date: row.due_date ?? row.original.due_date,
        priority: row.priority ?? row.original.order_priority,
        pce: row.pce ?? row.original.pce,
        lot_size: row.lot_size ?? row.original.lot_size,
        order_id: orderId,
      };
      const isItemIdNotUpdated =
        !row.item_id || row.item_id === row.original.item_id;
      const isQtyNotUpdated = !row.qty || Number(row.qty) === row.original.qty;
      const isStartDateNotUpdated =
        !row.start_date || row.original.start_date === row.start_date;
      const isDueDateNotUpdated =
        !row.due_date || row.original.due_date === row.due_date;
      const onlyWillOrderListUpdate =
        isItemIdNotUpdated &&
        isQtyNotUpdated &&
        isStartDateNotUpdated &&
        isDueDateNotUpdated;
      // const onlyObj = {
      //   order_id: orderId,
      //   order_name: row.order_name ?? row.original.order_name,
      //   color: row.color ?? row.original.color,
      // };
      if (onlyWillOrderListUpdate) {
        return handleUpdateOrder({ values: obj, table });
      } else if (beforeCalStatus) {
        return handleUpdateOrder({ values: obj, table });
      } else {
        return handleUpdateOrder({ values: obj, table });
        // deleteInsightwinOrderDataComplete(
        //   orderId,
        //   queryClient,
        //   setLoading,
        //   0
        // ).then(() => {
        //   return handleUpdateOrder({ values: obj, table });
        // });
      }
    },
    onPaginationChange: setPagination,
    state: {
      pagination,
      rowSelection: selectedRowIds,
    },
    onCreatingRowSave: handleCreateOrder,
    createDisplayMode: "modal",
    editDisplayMode: "modal",
    //  enableRowSelection: true,
    muiTableFooterProps: {
      sx: {
        background: "tomato",
      },
    },
    muiTableBodyCellProps: {
      sx: {
        fontSize: "14px",
        fontWeight: "500",
        padding: "10px !important",
        color: "#111",
      },
    },
    muiTableContainerProps: {
      sx: {
        width: "100%",
      },
    },
    muiTableProps: {
      sx: {
        borderCollapse: "collapse",
        width: "100%",
      },
    },
    muiSkeletonProps: {
      animation: "wave",
      height: 30,
    },
    muiCircularProgressProps: {
      color: "secondary",
      thickness: 5,
      size: 55,
    },
    muiTableHeadProps: {
      sx: {
        boxSizing: "border-box",
        "& tr": {
          boxSizing: "border-box",
          paddingBottom: 0,
          background: "#fff",
          color: "#111",
          borderTop: `1px solid ${COLORS.BORDER_BRIGHT}`,
          // borderBottom: `1px solid ${COLORS.BORDER_BRIGHT}`,
          "&: th": {
            textAlign: "",
            padding: 0,
          },
        },
      },
    },
    muiPaginationProps: {
      showRowsPerPage: false,
      shape: "rounded",
      sx: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
      },
    },
    // muiBottomToolbarProps: {
    //   sx: {
    //     position: "absolute !important",
    //     bottom: "48px",
    //     left: "50%",
    //     width: "inherit",
    //     transform: "translate(-50%, -50%)",
    //   },
    // },
    enablePagination: true,
    muiTableBodyProps: {
      sx: {
        width: "100%",
        "&: tr": {
          padding: "0 !important",
        },
      },
    },
    manualPagination: false,
    enableToolbarInternalActions: true,
    // paginationDisplayMode: "pages",
    muiToolbarAlertBannerProps: {
      content: "🚨 중요한 공지사항입니다.",
      color: "info",
      sx: { backgroundColor: "#1976D2 !important", color: "white" },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        //  color: "black",
        height: "60px",
        // backgroundColor: (() => {
        //   switch (row.original.status) {
        //     case 1:
        //       return "#f8f9fa";
        //     case 2:
        //       return "#cae6fa";
        //     // case 3:
        //     //   return "#cae6fa";
        //     case 3:
        //       return "#fce3e3";
        //     default:
        //       return "inherit";
        //   }
        // })(),
        cursor: "pointer",
        //   backgroundColor: "#fff",
        backgroundColor: row.original.is_excluded === true ? "#f0f0f0" : "#fff",
        borderBottom: "1px solid rgba(224, 224, 224, 0.5)",
        color: row.original.status === 0 ? "#868e96" : "black",
        // opacity:
        //   row.original.is_excluded === true ||
        //   ((row.original.is_excluded === true ||
        //     row.original.is_excluded === undefined) &&
        //     row.original.status === 3)
        //     ? "0.75"
        //     : 1,
      },
      onClick: (event) => {
        handleRowClick({ event, row });
      },
      //   selected: rowSelection[row.id],
    }),
    onRowSelectionChange: setRowSelection,
    muiTableHeadCellProps: {
      sx: {
        // borderBottom: "none",
        fontSize: "15px !important",
        paddingBottom: "0.75rem",
        color: "#111",
        fontWeight: "600",
      },
    },
    columnFilterDisplayMode: "popover",
    initialState: {
      // rowSelection: data.reduce((acc, row, index) => {
      //   acc[index] = true;
      //   return acc;
      // }, {}),
      // showColumnFilters: true,
      // pagination: { pageSize: 10 },
    },
    displayColumnDefOptions: {
      "mrt-row-select": {
        size: 50,
        enableResizing: false,
        muiTableHeadCellProps: {
          sx: {
            paddingLeft: "16px",
            opacity: 1,
          },
        },
        muiTableBodyCellProps: {
          sx: {
            paddingLeft: "16px",
            "& input": {
              opacity: 1,
              width: "20px",
              height: "20px",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            },
          },
        },
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "none !important",
        background: "none !important",
        borderBottom: `1px solid var(--mui-palette-divider)`,
        "& .css-qc99f7": {
          position: "relative !important",
        },
      },
    },
    muiSelectAllCheckboxProps: {
      sx: {
        "& input": {
          opacity: 1,
          width: "20px",
          height: "20px",
          top: "42%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        },
      },
    },
    enableTopToolbar: false,
    localization: localizationTextObject,
    renderEditRowDialogContent: ({ internalEditComponents, row, table }) => (
      <>
        <EditRowDialog
          table={table}
          row={row}
          itemData={item}
          initialData={row.original}
          handleUpdateOrder={handleUpdateOrder}
        />
      </>
    ),
    renderCreateRowDialogContent: ({ table, row, internalEditComponents }) => (
      <CreateRowDialog
        table={table}
        row={row}
        internalEditComponents={internalEditComponents}
        itemData={item}
        handleCreateOrder={handleCreateOrder}
        isEdit={false}
        openSnackbar={openSnackbar}
      />
    ),
  });
  useEffect(() => {
    tableInstanceRef.current = table;
  }, [table]);
  // const ToolbarActions = ({ table }) => {
  //   const selectedRows = table.getSelectedRowModel().rows;
  //   const hasSelection = selectedRows.length > 0;
  //   const handleBulkApply = () => {
  //     selectedRows.forEach((row) => {
  //       const orderId = row.original.order_id;
  //       row.original.is_excluded = false;
  //       updateRowExcludedStatus(orderId, false);
  //     });
  //     openSnackbar(`${selectedRows.length}개 항목을 적용으로 변경했습니다.`);
  //     setRowSelection({});
  //   };
  //   const handleBulkExclude = () => {
  //     selectedRows.forEach((row) => {
  //       const orderId = row.original.order_id;
  //       row.original.is_excluded = true;
  //       updateRowExcludedStatus(orderId, true);
  //     });
  //     openSnackbar(`${selectedRows.length}개 항목을 미적용으로 변경했습니다.`);
  //     setRowSelection({});
  //   };
  //   const handleBulkEdit = () => {
  //     setBulkEditModalOpen(true);
  //   };
  //   return (
  //     <div className="toolbar_container">
  //       <Button
  //         variant="contained"
  //         color="success"
  //         onClick={handleBulkApply}
  //         disabled={!hasSelection}
  //         size="small"
  //       >
  //         선택적용
  //       </Button>
  //       <Button
  //         variant="contained"
  //         color="warning"
  //         onClick={handleBulkExclude}
  //         disabled={!hasSelection}
  //         size="small"
  //       >
  //         선택미적용
  //       </Button>
  //       <AddButton
  //         onClick={() => {
  //           table.setCreatingRow(true);
  //         }}
  //       />
  //       <DeleteButton
  //         onClick={() => handleDeleteSelected(table)}
  //         disabled={!hasSelection}
  //       />
  //       <Button
  //         variant="contained"
  //         color="info"
  //         onClick={handleBulkEdit}
  //         disabled={!hasSelection}
  //         startIcon={<ModeEdit />}
  //         size="small"
  //       >
  //         선택 수정
  //       </Button>
  //     </div>
  //   );
  // };
  return {
    table,
    ToolbarActions,
    bulkEditModalOpen,
    setBulkEditModalOpen,
  };
}
